import React from 'react';
import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'sonner';
import { useBoolean } from 'react-use';
import { useTranslation } from 'react-i18next';
import { fetchCampaign, patchCampaign, postTrackingLink, updateCampaign } from '@/services/campaign';
import { putTrackingLink } from '@/services/tracking-link';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, } from '@/components/ui/dropdown-menu';
import { Copy, ExternalLink, Lock, MoreHorizontal, Pencil, Save } from 'lucide-react';
import { CampaignImageCard } from '@/components/card/campaign-image-card';
import { campaignSchema } from '@/schema/campaign-schema';
import { CampaignNotFound } from '@/components/not-found/campaign-not-found';
import { Loading } from '@/components/loader/loading';
import i18next from '@/i18next';
import { TrackingLinksTable } from '@/routes/console/_authenticated/_layout/campaigns/$publicId/_layout/-features/tracking-links-table';
import { FaqSection } from '@/routes/console/_authenticated/_layout/campaigns/$publicId/_layout/-features/faq-section';
import { TextField } from '@/components/text-field';
import { RichTextField } from '@/components/rich-text-field';
import { CustomSlugField } from '@/components/custom-slug-field';
import { MarkdownPreview } from '@/components/markdown-preview';
import { patchEntryPointCustomSlug } from '@/services/entry-point';
import { Form } from '@/components/ui/form';
import { CampaignEntryPoints } from '@/components/campaign-entry-points';
import { errorHandler } from '@/lib/utils';
import { FeedbackConfigCard } from '@/routes/console/_authenticated/_layout/campaigns/-features/feedback-config-card';
import { FeedbackQuestionsBuilder } from '@/routes/console/_authenticated/_layout/campaigns/-features/feedback-questions-builder';
import { AutoStoreConfigCard } from '@/routes/console/_authenticated/_layout/campaigns/-features/auto-store-config-card';
import { ShowroomConfigCard } from '@/routes/console/_authenticated/_layout/campaigns/-features/showroom-config-card';
import { SaveAsTemplateDialog } from '@/features/campaign-templates';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { planAutoStoreLinkSync, } from '@/routes/console/_authenticated/_layout/campaigns/$publicId/_layout/-features/auto-store-link-sync';
export const Route = createFileRoute('/console/_authenticated/_layout/campaigns/$publicId/_layout/')({
    component: CampaignPage,
    loader: ({ params: { publicId } }) => fetchCampaign(publicId),
    errorComponent: () => <CampaignNotFound />,
    pendingComponent: () => <Loading loadingText={i18next.t('campaigns.loading.campaign', { ns: 'glossary' })}/>,
});
function CampaignPage() {
    const campaign = Route.useLoaderData();
    const router = useRouter();
    const { t } = useTranslation();
    const { workspace } = useAuthenticationState();
    const [edit, toggleEdit] = useBoolean(false);
    const [saveTemplateOpen, setSaveTemplateOpen] = React.useState(false);
    const editFormRef = React.useRef(null);
    const revealEditFormRef = React.useRef(false);
    // The form renders inside the description card, below the fold. This runs
    // from the menu's onCloseAutoFocus rather than an effect: Radix hands focus
    // back to the ⋯ trigger after the menu unmounts, which would scroll the
    // page straight back to the top.
    const handleMenuCloseAutoFocus = React.useCallback((event) => {
        if (!revealEditFormRef.current) {
            return;
        }
        revealEditFormRef.current = false;
        event.preventDefault();
        const container = editFormRef.current;
        if (container === null) {
            return;
        }
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        container.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        container.querySelector('input')?.focus({ preventScroll: true });
    }, []);
    const form = useForm({
        resolver: yupResolver(campaignSchema),
        defaultValues: {
            name: '',
            description: '',
        },
    });
    // The campaign's vanity URL is a column on its default smart link, so
    // saving the edit form touches two endpoints. `handleSave` sequences them.
    const defaultEntryPoint = campaign.entryPoints?.find((ep) => ep.isDefault);
    const { mutateAsync: saveCampaign, isPending: isSavingCampaign } = useMutation({
        mutationFn: updateCampaign,
        onError: errorHandler,
    });
    const { mutateAsync: saveCustomSlug, isPending: isSavingSlug } = useMutation({
        mutationFn: patchEntryPointCustomSlug,
        onError: errorHandler,
    });
    // Sequential on purpose: two creates in parallel would race for the same
    // next sort order on the server.
    const { mutateAsync: syncStoreLinks, isPending: isSyncingStoreLinks } = useMutation({
        mutationFn: async (changes) => {
            for (const change of changes) {
                if (change.kind === 'update') {
                    await putTrackingLink({ publicId: change.publicId, data: change.data });
                }
                else {
                    await postTrackingLink({ publicId: campaign.publicId, data: change.data });
                }
            }
        },
        onError: errorHandler,
    });
    const isMutating = isSavingCampaign || isSavingSlug || isSyncingStoreLinks;
    const { mutate: patch, isPending: isUpdatingStatus } = useMutation({
        mutationFn: patchCampaign,
        onSuccess: (data) => {
            toast.success(data.message);
            router.invalidate();
        },
        onError: errorHandler,
    });
    React.useEffect(() => {
        if (campaign) {
            const iosLink = campaign.trackingLinks?.find((link) => link.context === 'ios');
            const androidLink = campaign.trackingLinks?.find((link) => link.context === 'android');
            form.reset({
                name: campaign.name,
                description: campaign.description ?? '',
                customSlug: campaign.entryPoints?.find((ep) => ep.isDefault)?.customSlug ?? '',
                mode: campaign.mode,
                fallbackUrl: campaign.fallbackUrl ?? '',
                appStoreUrl: iosLink?.targetUrl ?? '',
                googlePlayUrl: androidLink?.targetUrl ?? '',
                feedbackTitle: campaign.feedbackTitle ?? '',
                feedbackThankYouMessage: campaign.feedbackThankYouMessage ?? '',
                feedbackTelegramChatId: campaign.feedbackTelegramChatId ?? '',
                feedbackCollectName: campaign.feedbackCollectName ?? true,
                feedbackCollectEmail: campaign.feedbackCollectEmail ?? true,
                feedbackQuestions: campaign.feedbackQuestions ?? [],
                showroomTagline: campaign.showroomTagline ?? '',
                showroomCurrency: campaign.showroomCurrency ?? 'USD',
                showroomCallNumber: campaign.showroomCallNumber ?? '',
                showroomWhatsappNumber: campaign.showroomWhatsappNumber ?? '',
                showroomTelegramHandle: campaign.showroomTelegramHandle ?? '',
                showroomDirectionsUrl: campaign.showroomDirectionsUrl ?? '',
            });
        }
    }, [campaign, form]);
    const onSubmit = React.useCallback(async (data) => {
        // `customSlug` is stripped here on purpose: it's an EntryPoint
        // column, and the campaign PUT would reject it as an extra field.
        // `trackingLinks` never rides on the PUT: the server binds that
        // collection by position and deletes whatever is missing, so the
        // store links go through their own endpoints instead (#282).
        const { appStoreUrl, googlePlayUrl, customSlug, trackingLinks: _trackingLinks, ...payload } = data;
        try {
            const result = await saveCampaign({ publicId: campaign.publicId, data: payload });
            if (data.mode === 'auto_store') {
                await syncStoreLinks(planAutoStoreLinkSync(campaign.trackingLinks ?? [], { appStoreUrl, googlePlayUrl }));
            }
            // Sequenced after the campaign save so a rejected slug (taken
            // or malformed) can't take the rest of the user's edits down
            // with it — they keep the save and only have the slug to fix.
            const nextSlug = (customSlug ?? '').trim();
            if (defaultEntryPoint && nextSlug !== (defaultEntryPoint.customSlug ?? '')) {
                await saveCustomSlug({
                    publicId: defaultEntryPoint.publicId,
                    customSlug: nextSlug === '' ? null : nextSlug,
                });
            }
            toast.success(result.message);
            router.invalidate();
            toggleEdit(false);
        }
        catch {
            // Both mutations surface their own error through `errorHandler`.
            // Leave edit mode open so the rejected field can be corrected.
        }
    }, [
        saveCampaign,
        syncStoreLinks,
        saveCustomSlug,
        campaign.publicId,
        campaign.trackingLinks,
        defaultEntryPoint,
        router,
        toggleEdit,
    ]);
    const handleChangeStatus = React.useCallback((status) => {
        patch({ publicId: campaign.publicId, data: { status } });
    }, [campaign.publicId, patch]);
    const statusVariant = campaign.status === 'active' ? 'default' : campaign.status === 'draft' ? 'secondary' : 'outline';
    const heroSrc = campaign.imageDetailUrl ?? campaign.imageUrl;
    const entryPointCount = campaign.entryPoints?.length ?? 0;
    const trackingLinkCount = campaign.trackingLinks?.length ?? 0;
    const liveUrl = defaultEntryPoint?.publicUrl ?? campaign.publicUrl;
    // Mirrors the copy affordance in <CampaignEntryPoints>; clipboard access
    // rejects on insecure origins and when the user denies permission, so the
    // failure has to be surfaced rather than swallowed.
    const handleCopyLiveUrl = React.useCallback(() => {
        if (!liveUrl)
            return;
        navigator.clipboard
            .writeText(liveUrl)
            .then(() => toast.success(t('copied_clipboard')))
            .catch(() => toast.error(t('copy_failed', { ns: 'error' })));
    }, [liveUrl, t]);
    return (<div className="p-6 space-y-6">
            {/* HERO BANNER */}
            <Card className="relative overflow-hidden border-border/60 p-0">
                <div className="relative isolate h-56 sm:h-64">
                    {heroSrc ? (<img src={heroSrc} alt={campaign.name} className="absolute inset-0 h-full w-full object-cover" loading="lazy"/>) : (<div className="absolute inset-0 bg-gradient-to-br from-muted via-muted/40 to-background"/>)}
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/10"/>

                    <div className="relative z-10 flex h-full flex-col justify-end gap-3 p-5 sm:p-6">
                        <div className="flex flex-wrap items-center gap-2">
                            <Badge variant={statusVariant} className="capitalize">
                                {campaign.status}
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                                {campaign.mode.replace('_', ' ')}
                            </Badge>
                        </div>
                        <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                            {campaign.name}
                        </h1>
                    </div>

                    <div className="absolute right-4 top-4 z-10 flex flex-wrap items-center justify-end gap-2">
                        {/* Default entry point's publicUrl is the canonical scan URL (ep-{id}).
            Fall through to campaign.publicUrl (legacy ad-{id} slug — still
            resolves via handleLegacyCampaign) only if no default EP exists,
            which shouldn't happen post-create. */}
                        {liveUrl ? (<Button asChild size="sm" variant="secondary">
                                <a href={liveUrl} target="_blank" rel="noopener noreferrer">
                                    <ExternalLink className="mr-1 h-4 w-4"/>
                                    {t('campaigns.actions.view', { ns: 'glossary', defaultValue: 'View' })}
                                </a>
                            </Button>) : null}

                        {['draft', 'archived'].includes(campaign.status) && (<Button size="sm" onClick={() => handleChangeStatus('active')} disabled={isUpdatingStatus}>
                                {t('campaigns.actions.activate', { ns: 'glossary', defaultValue: 'Activate' })}
                            </Button>)}

                        {campaign.status === 'active' && (<Button size="sm" variant="secondary" onClick={() => handleChangeStatus('archived')} disabled={isUpdatingStatus}>
                                {t('campaigns.actions.archive', { ns: 'glossary', defaultValue: 'Archive' })}
                            </Button>)}

                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="secondary" size="sm" aria-label={t('more_actions', { defaultValue: 'More actions' })}>
                                    <MoreHorizontal className="h-4 w-4"/>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56" onCloseAutoFocus={handleMenuCloseAutoFocus}>
                                <DropdownMenuItem onSelect={() => {
            revealEditFormRef.current = true;
            toggleEdit(true);
        }}>
                                    <Pencil className="mr-2 h-4 w-4"/>
                                    {t('edit')}
                                </DropdownMenuItem>
                                {workspace?.subscription?.plan?.allowClone ? (<DropdownMenuItem asChild>
                                        <Link to="/console/campaigns/new" search={{ cloneFrom: campaign.publicId }}>
                                            <Copy className="mr-2 h-4 w-4"/>
                                            {t('clone')}
                                        </Link>
                                    </DropdownMenuItem>) : (<DropdownMenuItem disabled>
                                        <Lock className="mr-2 h-4 w-4"/>
                                        {t('clone')}
                                    </DropdownMenuItem>)}
                                {workspace?.publicId ? (<>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem disabled={!workspace.subscription?.plan?.allowClone} onSelect={() => setSaveTemplateOpen(true)}>
                                            {workspace.subscription?.plan?.allowClone ? (<Save className="mr-2 h-4 w-4"/>) : (<Lock className="mr-2 h-4 w-4"/>)}
                                            {t('save_as_template.button', {
                ns: 'campaign',
                defaultValue: 'Save as template',
            })}
                                        </DropdownMenuItem>
                                    </>) : null}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {workspace?.publicId ? (<SaveAsTemplateDialog workspaceId={workspace.publicId} sourceCampaignId={campaign.publicId} defaultName={campaign.name} locked={!workspace.subscription?.plan?.allowClone} open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}/>) : null}
                    </div>
                </div>

                <div className="border-t border-border/40 bg-card/60 px-5 py-4 sm:px-6">
                    <CampaignEntryPoints campaign={campaign}/>
                </div>
            </Card>

            {/* META + DESCRIPTION GRID */}
            <div className="grid gap-4 lg:grid-cols-3">
                <Card className="border-border/60 lg:col-span-2">
                    <CardContent className="space-y-4 p-6">
                        <div className="space-y-1">
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">
                                {t('campaigns.description', { ns: 'glossary' })}
                            </p>
                            {campaign.description ? (<MarkdownPreview source={campaign.description} className="max-w-3xl text-sm text-muted-foreground"/>) : (<p className="text-sm italic text-muted-foreground/70">
                                    {t('no_description', { defaultValue: 'No description yet.' })}
                                </p>)}
                        </div>

                        {edit && (<div ref={editFormRef} className="scroll-mt-6 space-y-4 border-t border-border/60 pt-4">
                                <Form {...form}>
                                    <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                                        <TextField control={form.control} name="name" placeholder={t('campaigns.placeholders.name', { ns: 'glossary' })} label={t('campaign_name')} disabled={isMutating} autoComplete="campaign-name"/>
                                        <CustomSlugField control={form.control} name="customSlug" label={t('campaigns.custom_slug.label', {
                ns: 'glossary',
                defaultValue: 'Custom URL',
            })} disabled={isMutating} excludeEntryPointId={defaultEntryPoint?.publicId}/>
                                        <RichTextField control={form.control} name="description" placeholder={t('campaigns.placeholders.description', { ns: 'glossary' })} label={t('description')} disabled={isMutating}/>
                                    </form>
                                    {campaign.mode === 'feedback' && (<>
                                            <FeedbackConfigCard control={form.control} disabled={isMutating} workspaceId={workspace?.publicId}/>
                                            <FeedbackQuestionsBuilder control={form.control} name="feedbackQuestions" disabled={isMutating}/>
                                        </>)}
                                    {campaign.mode === 'auto_store' && (<AutoStoreConfigCard control={form.control} disabled={isMutating}/>)}
                                    {campaign.mode === 'showroom' && (<ShowroomConfigCard control={form.control} disabled={isMutating}/>)}
                                </Form>
                                <div className="flex flex-col gap-2 lg:flex-row">
                                    <Button className="flex-1" variant="outline" onClick={() => toggleEdit(false)} disabled={isMutating}>
                                        {t('cancel')}
                                    </Button>
                                    <Button className="flex-1" onClick={form.handleSubmit(onSubmit)} disabled={isMutating}>
                                        {t('update')}
                                    </Button>
                                </div>
                            </div>)}
                    </CardContent>
                </Card>

                <div className="space-y-4">
                    <Card className="border-border/60">
                        <CardContent className="space-y-4 p-5">
                            <p className="text-xs uppercase tracking-wide text-muted-foreground">
                                {t('campaigns.at_a_glance', { defaultValue: 'At a glance' })}
                            </p>
                            <dl className="grid grid-cols-2 gap-3 text-sm">
                                <div className="space-y-0.5">
                                    <dt className="text-xs text-muted-foreground">
                                        {t('campaigns.entry_points', { defaultValue: 'Entry points' })}
                                    </dt>
                                    <dd className="text-xl font-semibold tabular-nums text-foreground">
                                        {entryPointCount}
                                    </dd>
                                </div>
                                <div className="space-y-0.5">
                                    <dt className="text-xs text-muted-foreground">
                                        {t('campaigns.tracking_links', { defaultValue: 'Tracking links' })}
                                    </dt>
                                    <dd className="text-xl font-semibold tabular-nums text-foreground">
                                        {trackingLinkCount}
                                    </dd>
                                </div>
                                {/* The live URL, read-only. `publicUrl` already
            resolves to the vanity slug when one is set
            (EntryPoint::getPublicSlug), so this is the
            exact string a customer scans — worth being
            able to read and copy without opening the
            editor. */}
                                {liveUrl ? (<div className="space-y-0.5 col-span-2">
                                        <dt className="text-xs text-muted-foreground">
                                            {t('campaigns.public_link.label', {
                ns: 'glossary',
                defaultValue: 'Public link',
            })}
                                        </dt>
                                        <dd className="flex items-center gap-1">
                                            <a href={liveUrl} target="_blank" rel="noopener noreferrer" className="truncate font-mono text-xs text-primary underline underline-offset-2">
                                                {liveUrl.replace(/^https?:\/\//, '')}
                                            </a>
                                            <Button variant="ghost" size="icon" className="size-6 shrink-0" aria-label={t('copy_link')} onClick={handleCopyLiveUrl}>
                                                <Copy className="size-3"/>
                                            </Button>
                                        </dd>
                                    </div>) : null}
                                <div className="space-y-0.5 col-span-2">
                                    <dt className="text-xs text-muted-foreground">
                                        {t('campaigns.public_id', { defaultValue: 'Public ID' })}
                                    </dt>
                                    <dd>
                                        <Badge variant="outline" className="font-mono text-xs">
                                            ad-{campaign.publicId}
                                        </Badge>
                                    </dd>
                                </div>
                            </dl>
                        </CardContent>
                    </Card>

                    <CampaignImageCard campaign={campaign}/>
                </div>
            </div>

            {(campaign.mode === 'classic' || campaign.mode === 'showroom' || campaign.mode === 'auto_store') && (<TrackingLinksTable campaign={campaign}/>)}

            {/* Page-design / background-animation picker lives on the
            Customize tab — see _layout.tsx for the tab nav. The shortcut
            Card that used to live here has been removed since the tab
            strip is the canonical entry point. */}

            <FaqSection />
        </div>);
}
